import React, { useState, useEffect } from "react";
import { PageLayout } from "../../../../components";
import { FilePicker, ImageUploadDisplay } from "../../components";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  formatImagesForPost,
  getIsInValid,
  hasImageBeenUploaded
} from "../../helpers";
import {
  getAppraisalData,
  selectImageUploadData,
  getRequiredPhotosForGroup,
  getSourceSystemIsCompactView
} from "../../../../store/selectors";
import { imageUploadSlice } from "../../../../store/slices/ImageUploads";
import { removePhoto } from "../../../../store/slices/Appraisal";
import { useSelector as useAxiosSelector } from "../../context/axiosContext";
import { PhotoUploadError } from "../../components/PhotoUploadError";

export default function UploadProgress({
  handleChange,
  handleSubmit,
  minPhotos,
  currentUploadGroup,
  loading,
  lastImage,
  hasAllImages
}) {
  const {
    retrying,
    currentUploads,
    returnToGallery,
    images,
    currentImageIdentifiers,
    maxUploads,
    currentUploadHelper,
    uploadValidationError,
    mobileImageLastAction
  } = useSelector(selectImageUploadData);
  const { singlePhotoUploadMore } = currentUploadHelper;

  const { cancelToken } = useAxiosSelector((state) => state);

  const { appraisalSessionId, images: appraisalImages } =
    useSelector(getAppraisalData);

  const [showUploadMore, setShowUploadMore] = useState(
    singlePhotoUploadMore ? true : false
  );
  const [showAreYouHappy, setShowAreYouHappy] = useState(false);
  const [fileUploadValidation, setFileUploadValidation] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const requiredPhotos = useSelector(
    getRequiredPhotosForGroup(currentUploadHelper)
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isCompactView = useSelector(getSourceSystemIsCompactView);

  const RETRYING = retrying > 0;
  const MAX_RETRIES = retrying === 6;

  const imagesToRender = returnToGallery
    ? images.filter((image) => image.group === currentUploadGroup).slice(-1)
    : images.filter((image) => image.group === currentUploadGroup);

  useEffect(() => {
    // handles back navigation from retake to avoid empty screen
    // also prevents upload more button showing for a second before the current uploads array is populated in the request
    const timer = setTimeout(() => {
      if (imagesToRender.length === 0 && currentUploads.length === 0) {
        setShowUploadMore(true);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [currentUploads.length, imagesToRender.length]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (requiredPhotos <= 0 || minPhotos === 1) {
        setShowAreYouHappy(true);
      }
      if (minPhotos > 1 && currentImageIdentifiers.length > 0) {
        setShowValidation(true);
        setShowUploadMore(true);
      }
    }, 700);
    return () => clearTimeout(timer);
  }, [currentImageIdentifiers.length, minPhotos, requiredPhotos]);

  useEffect(() => {
    if (MAX_RETRIES) {
      dispatch(imageUploadSlice.actions.clearCurrentUploads());
      dispatch(imageUploadSlice.actions.removeUnsuccessfulImages());
      cancelToken.cancel("operation cancelled");
    }
  }, [MAX_RETRIES, cancelToken, dispatch]);

  function handleRetake(image) {
    setShowUploadMore(true);
    dispatch(
      imageUploadSlice.actions.removeCurrentImageIdentifier(
        image.imageIdentifier
      )
    );
    dispatch(imageUploadSlice.actions.setMaxUploads(maxUploads + 1));

    // user is going through the journey from start and has possibly not saved image to appraisal yet
    if (!returnToGallery) {
      dispatch(imageUploadSlice.actions.deleteImage(image));

      // user has delete last image on list so is navigated back to helper page
      if (currentUploads.length === 1) {
        navigate(`/image-upload/${currentUploadGroup}`);
      }
    }

    // user has navigated back to upload page from gallery and has deleted image after initial retake from gallery
    if (returnToGallery) {
      dispatch(
        imageUploadSlice.actions.flagImageForDeletion({
          image,
          flaggedForDeletion: true
        })
      );

      setShowAreYouHappy(false);
    }

    // user has back navigated so there is no current live cancel token
    if (cancelToken) {
      cancelToken.cancel("operation cancelled");
    }

    // user has back navigated after image has saved to appraisal
    const imageIsSavedToAppraisal = appraisalImages.some(
      (img) => img.imageIdentifier === image.imageIdentifier
    );

    if (imageIsSavedToAppraisal) {
      setShowUploadMore(true);
      dispatch(
        removePhoto({
          imageIdentifier: image.imageIdentifier,
          appraisalSessionId
        })
      );
    }
  }

  const onChange = (images, mobileImageLastAction) => {
    dispatch(
      imageUploadSlice.actions.setMobileImageLastAction(mobileImageLastAction)
    );
    const validationWarning = getIsInValid(images, maxUploads);
    if (!validationWarning) {
      const formattedImages = formatImagesForPost(currentUploadHelper, images);
      handleChange(formattedImages);
      dispatch(
        imageUploadSlice.actions.setMaxUploads(maxUploads - images.length)
      );

      setShowUploadMore(false);
    } else {
      setFileUploadValidation(validationWarning);
    }
  };

  return (
    <PageLayout>
      <PageLayout.Header
        title="Photograph vehicle"
        step={isCompactView ? 6 : 12}
      />
      <PageLayout.Content>
        {uploadValidationError || MAX_RETRIES ? (
          <PhotoUploadError />
        ) : (
          <>
            {showValidation && requiredPhotos > 0 ? (
              <p className="ch-fs--4">
                Great, add <b> {requiredPhotos}</b> more photo
                {requiredPhotos > 1 ? "s" : ""}.
              </p>
            ) : (
              showAreYouHappy &&
              imagesToRender.length > 0 && (
                <p className="ch-fs--4">
                  Are you happy with{" "}
                  {imagesToRender.length > 1 ? "these" : "this"} photo
                  {imagesToRender.length > 1 ? "s" : ""}?
                </p>
              )
            )}
            <>
              {showUploadMore && !returnToGallery && (
                <FilePicker
                  buttonLabel="Upload another photo"
                  buttonClass="ch-btn--primary ch-btn--block ch-mv--4"
                  handleChange={onChange}
                  uploadMore
                  mobileAction={mobileImageLastAction}
                />
              )}
              {imagesToRender
                .map((image) => {
                  const isAlreadyUploaded = hasImageBeenUploaded(image);
                  return (
                    <ImageUploadDisplay
                      retrying={RETRYING && !isAlreadyUploaded}
                      image={image}
                      cancelUpload={() => handleRetake(image)}
                      key={
                        image.imageSrc ? image.imageSrc : image.imageIdentifier
                      }
                      showUploadMore
                      multifilestackInProgress={
                        currentUploads.length > 1 &&
                        images.some((image) => image.progress < 100)
                      }
                    />
                  );
                })
                .reverse()}
            </>
          </>
        )}
        {fileUploadValidation && (
          <div className="ch-form__group ch-form__group--error">
            <span className="ch-form__control-validation ">
              {fileUploadValidation}
            </span>
          </div>
        )}
      </PageLayout.Content>
      <PageLayout.Footer>
        {uploadValidationError || MAX_RETRIES ? (
          <FilePicker
            buttonLabel={
              uploadValidationError || MAX_RETRIES
                ? "Try again"
                : "Upload photo"
            }
            buttonClass="ch-btn--success ch-btn--block ch-mh--auto ch-width--12 sl:ch-width--8 sm:ch-width--4"
            handleChange={(images) => {
              onChange(images);
            }}
            mobileAction={mobileImageLastAction}
          />
        ) : (
          <>
            <button
              className="ch-btn ch-btn--success ch-btn--block ch-mh--auto sm:ch-width--6 md:ch-width--5 ch-width--12 lg:ch-width--4"
              disabled={
                images.some((image) => image.progress < 100) || !hasAllImages
              }
              onClick={handleSubmit}
            >
              {loading ? (
                <span className="btn-loader" />
              ) : hasAllImages && currentImageIdentifiers.length === 0 ? (
                "Continue"
              ) : (
                "Save and continue"
              )}
            </button>
          </>
        )}
      </PageLayout.Footer>
    </PageLayout>
  );
}
